import { Joyride, Step, TooltipRenderProps } from "react-joyride";
import { Button } from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, matchPath, useNavigate } from "react-router";
import usePersonAuthStore from "../../store/personAuthStore";
import usePersonStore from "../../store/personStore";
import { useMainMenuContext } from "../MainMenu/MainMenuProvider";
import { useTourContext } from "../../../context/TourContext";
import { dashboardSteps } from "./DashboardSteps";
import { mainMenuSteps } from "./MainMenuSteps";
import { reportsSteps } from "./ReportsSteps";
import { studentInsightsSteps } from "./StudentInsightsSteps";

type TourSection = { label: string; steps: Step[]; onSkip?: string };
type KeyedSection = TourSection & { key: string };

const portalTour: Record<string, TourSection> = {
    ...dashboardSteps,
    ...mainMenuSteps,
    ...reportsSteps,
    ...studentInsightsSteps,
};

const isTransition = (key: string) => key.includes("nextSection");

// Menu must be open for steps in a menu section (or steps flagged data.menu)
const stepNeedsMenu = (step?: Step, section?: TourSection) =>
    !!step && (step.data?.menu ?? section?.onSkip === "mainMenu");

// Transition steps live on the dashboard unless they say otherwise
const stepRoute = (step?: Step, section?: KeyedSection) =>
    (step?.data?.route as string | undefined) ??
    (section && isTransition(section.key) ? "/dashboard" : undefined);

const hasParam = (route?: string) => !!route && /:[^/]+/.test(route);
const resolveRoute = (route: string, demoStudentId?: string | number) =>
    route.replace(/:[^/]+/g, String(demoStudentId));

type CustomTooltipProps = TooltipRenderProps & {
    sectionStartIndexes: number[];
    sectionJumpTargets: (number | undefined)[];
    steps: Step[];
    onNextSection: (stepIndex: number) => void;
};

const CustomToolTip = ({
    backProps, index, primaryProps, skipProps, step, steps,
    tooltipProps, sectionStartIndexes, sectionJumpTargets, onNextSection,
}: CustomTooltipProps) => {
    const isLastStep = index === steps.length - 1;

    let currentSectionIndex = 0;
    sectionStartIndexes.forEach((start, i) => {
        if (start <= index) currentSectionIndex = i;
    });
    const nextSectionStart = sectionJumpTargets[currentSectionIndex];

    return (
        <div
            {...tooltipProps}
            style={{
                background: "#fff",
                borderRadius: 8,
                boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
                maxWidth: 400,
                padding: 24,
            }}
        >
            <div style={{ marginBottom: 20 }}>
                {step.title && (
                    <div style={{ textAlign: "center", fontSize: 18, fontWeight: 600, marginBottom: 10 }}>
                        {step.title}
                    </div>
                )}
                <div style={{ textAlign: "center", fontSize: 14, lineHeight: 1.6 }}>
                    {step.content}
                </div>
            </div>

            <div style={{ alignItems: "center", display: "flex", justifyContent: "space-between", gap: 8 }}>
                <div>
                    {index > 0 && <Button {...backProps} size="small">Back</Button>}
                    <Button {...skipProps} size="small" style={{ color: "#666" }}>Skip tour</Button>
                </div>

                {nextSectionStart !== undefined && (
                    <Button size="small" onClick={() => onNextSection(nextSectionStart)}>
                        Next section
                    </Button>
                )}

                <Button {...primaryProps} variant="contained" color="primary">
                    {isLastStep ? "Close" : "Next"}
                </Button>
            </div>
        </div>
    );
};

const PortalTour = () => {
    const firstSignIn = usePersonAuthStore(state => state.firstSignIn);
    const roles = usePersonAuthStore(state => state.roles);
    const totalUnreadWorkLists = usePersonStore(state => state.totalUnreadWorkLists);
    const demoStudentId = usePersonStore(state => state.company.defaultLocation?.demoStudent?.id);
    const { runTour, stopTutorial, tutorialSection, completeFirstTutorial, firstTutorialCompleted } = useTourContext();
    const locations = usePersonStore(state => state.locations)
    const { openMenu, toggleMenu } = useMainMenuContext();
    // const [firstTutorialCompleted, setFirstTutorialCompleted] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();
    const [stepIndex, setStepIndex] = useState(0);

    const active = !!runTour || (!!firstSignIn && !firstTutorialCompleted);
    // const active = !!runTour
    // const active = true
    const sections = useMemo<KeyedSection[]>(
        () =>
            Object.entries(portalTour)
                .filter(([key]) => !tutorialSection || key === tutorialSection)
                .map(([key, section]) => ({
                    ...section,
                    key,
                    steps: section.steps.filter(step => {
                        if (step.data?.access && !step.data.access(roles)) return false;
                        const showSample = firstSignIn || totalUnreadWorkLists > 0;
                        if (step.data?.condition === "showSample" && !showSample) return false;
                        if (step.data?.condition === "showReal" && showSample) return false;
                        if (step.data?.condition === "location" && locations.length < 2) return false;
                        if (hasParam(step.data?.route) && !demoStudentId) return false;
                        return true;
                    }),
                }))
                .filter(section => section.steps.length > 0),
        [tutorialSection, roles, demoStudentId]
    );

    const { steps, stepSection, sectionStartIndexes, sectionJumpTargets } = useMemo(() => {
        const steps: Step[] = [];
        const stepSection: KeyedSection[] = [];
        const sectionStartIndexes: number[] = [];

        sections.forEach(section => {
            sectionStartIndexes.push(steps.length);
            section.steps.forEach(step => {
                steps.push(step);
                stepSection.push(section);
            });
        });

        // Normal section: jump to the next section (transition included, so it is displayed).
        // Transition section: skip the following section and jump to the one after it.
        const sectionJumpTargets = sections.map((section, i) => {
            const offset = isTransition(section.key) ? 2 : 1;
            return sectionStartIndexes[i + offset];
        });

        return { steps, stepSection, sectionStartIndexes, sectionJumpTargets };
    }, [sections]);

    // Restart only when a tour starts/stops or the section changes
    useEffect(() => {
        if (!active || !steps.length) return;

        setStepIndex(0);

        const needsMenu = stepNeedsMenu(
            steps[0],
            stepSection[0]
        );

        if (needsMenu !== openMenu) {
            toggleMenu();
        }
    }, [tutorialSection, active]);

    const current = steps[stepIndex];
    const currentRoute = stepRoute(current, stepSection[stepIndex]);
    const onRoute = !currentRoute || !!matchPath(currentRoute, location.pathname);

    // Routes only. The menu is handled in goTo below.
    useEffect(() => {
        if (!active || !current || !currentRoute || onRoute) return;
        // if (currentRoute && !onRoute) {
        navigate(resolveRoute(currentRoute, demoStudentId));
        // }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [active, stepIndex, onRoute, current, currentRoute]);

    // Single place that moves the tour: sets menu, navigates, then sets the index
    const goTo = (index: number) => {
        const target = steps[index];
        if (!target) return;
        const section = stepSection[index];

        // menu
        const needsMenu = stepNeedsMenu(target, section);
        if (needsMenu && !openMenu) {
            toggleMenu();
        } else if (!needsMenu && openMenu) {
            toggleMenu();
        }
        // route
        const route = stepRoute(target, section);
        if (route && !matchPath(route, location.pathname)) {
            navigate(resolveRoute(route, demoStudentId));
        }

        setStepIndex(index);
    };

    const finish = () => {
        completeFirstTutorial()
        if (openMenu) toggleMenu();
        stopTutorial();
    };

    const renderTooltip = useCallback(
        (props: TooltipRenderProps) => (
            <CustomToolTip
                {...props}
                steps={steps}
                sectionStartIndexes={sectionStartIndexes}
                sectionJumpTargets={sectionJumpTargets}
                onNextSection={goTo}
            />
        ),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [steps, sectionStartIndexes, sectionJumpTargets, openMenu, location.pathname]
    );
    const run = active && !!current && onRoute;
    return (
        <Joyride
            scrollToFirstStep={true}

            run={run}
            stepIndex={stepIndex}
            steps={steps}
            continuous
            onEvent={data => {
                if (data.status === "finished" || data.status === "skipped") {
                    finish();
                    return;
                }
                if (data.type !== "step:after") return;

                // X button or Close: end the tour
                if (data.action === "close") {
                    finish();
                    return;
                }

                // ignore programmatic index changes (e.g. "Next section")
                if (data.index !== stepIndex) return;

                if (data.action === "prev") {
                    goTo(data.index - 1);
                    return;
                }

                // forward (next / primary) from the last step: done
                if (data.index >= steps.length - 1) {
                    finish();
                    return;
                }

                // leaving the transition step: go to the reports page first
                if (data.step.data?.action === "goToReports") {
                    navigate(`/dashboard/student/${demoStudentId}/sessions`);
                }
                goTo(data.index + 1);
            }}
            options={{ zIndex: 9999, showProgress: true, skipBeacon: true }}
            tooltipComponent={renderTooltip}
        />
    );
};

export default PortalTour;